import { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  CircularProgress,
  InputAdornment,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import api_endpoints from "../../../api";
import {
  useApiClientQuery,
  useApiClientMutation,
} from "../../../hooks/useApiClient";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { useSuccessAlert } from "../../../context/SuccessAlertContext/SuccessAlertContext";
import { AddPaymentMethodModal } from "./AddPaymentMethodModal";
import { AddCustomerModal } from "./AddCustomerModal";
import ApplyPaymentPlanModal from "./ApplyPaymentPlanModal";
import AuthContext from "../../../context/AuthContext/AuthContext";

/* Debounce search input for server calls */
function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* Build display label for donor rows */
function labelForDonor(d) {
  const name = `${d.first_name || ""} ${d.last_name || ""}`.trim();
  const company = d.company ? ` · ${d.company}` : "";
  const email = d.email ? ` · ${d.email}` : "";
  return `${name || d.email || d.phone || "Unknown"}${company}${email}`;
}

/* Build display label for payment method rows */
function labelForPaymentMethod(pm) {
  const type = pm.pm_type || pm.instrument_type;
  if (type === "card") {
    const last = pm.lastNumbers || pm.last4 || "????";
    const holder = pm.cardholderName ? `${pm.cardholderName} · ` : "";
    const exp = pm.expiration ? ` · ${pm.expiration}` : "";
    return `${holder}Card •••• ${last}${exp}`;
  }
  const bank = pm.bank_name || "Bank";
  const last = pm.last4 || pm.routing_last4 || "????";
  return `${bank} •••• ${last}`;
}

const isCard = (pm) => pm?.pm_type === "card" || pm?.instrument_type === "card";

const PLAN_FREQUENCY_LABEL = {
  WEEK: "Weekly",
  WEEKLY: "Weekly",
  BIWEEK: "BiWeekly",
  BIWEEKLY: "BiWeekly",
  MONTH: "Monthly",
  MONTHLY: "Monthly",
  QUARTER: "Quarterly",
  QUARTERLY: "Quarterly",
  YEAR: "Yearly",
  YEARLY: "Yearly",
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function parseInputDate(value) {
  if (!value) return null;
  const candidate = new Date(value);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
}

function addInterval(date, frequency) {
  const base = new Date(date);
  switch (frequency) {
    case "Weekly":
      base.setDate(base.getDate() + 7);
      break;
    case "BiWeekly":
      base.setDate(base.getDate() + 14);
      break;
    case "Monthly":
      base.setMonth(base.getMonth() + 1);
      break;
    case "Quarterly":
      base.setMonth(base.getMonth() + 3);
      break;
    case "Yearly":
      base.setFullYear(base.getFullYear() + 1);
      break;
    default:
      base.setMonth(base.getMonth() + 1);
      break;
  }
  return base;
}

/* End date must be >= start date (basic sanity). Ignore if continuous or end empty. */
function isDateRangeChronologicallyValid(startDate, endDate, isContinuous) {
  if (isContinuous) return true;
  if (!startDate || !endDate) return true;
  const start = parseInputDate(startDate);
  const end = parseInputDate(endDate);
  if (!start || !end) return true;
  return end >= start;
}

/* Generic: if end date is defined, it must be at least one full period after start. */
function isFrequencyRangeLongEnough(frequency, startDate, endDate, isContinuous) {
  if (isContinuous) return true;
  if (!startDate || !endDate) return true;

  const start = parseInputDate(startDate);
  const end = parseInputDate(endDate);
  if (!start || !end) return true;

  const normalized =
    PLAN_FREQUENCY_LABEL[String(frequency || "").toUpperCase()] || frequency;
  if (!normalized) return true;

  const minEnd = addInterval(start, normalized);
  return end >= minEnd;
}

function computeMonthlyAnchorDate(start, anchorDay) {
  if (!start || !anchorDay) return null;
  const clamped = Math.min(Math.max(anchorDay, 1), 28);
  const result = new Date(start);
  result.setDate(clamped);
  if (result < start) {
    result.setMonth(result.getMonth() + 1);
  }
  return result;
}

function computeChargeNowProrationPreview(start, anchorDay, amount) {
  if (!start || !anchorDay) return null;
  const clamped = Math.min(Math.max(anchorDay, 1), 28);
  const baseStart = new Date(start);
  if (Number.isNaN(baseStart.getTime())) return null;

  const anchorDate = computeMonthlyAnchorDate(baseStart, clamped);
  if (!anchorDate) return null;

  const previousAnchor = new Date(anchorDate);
  previousAnchor.setMonth(previousAnchor.getMonth() - 1);

  const startMidnight = new Date(
    baseStart.getFullYear(),
    baseStart.getMonth(),
    baseStart.getDate()
  );
  const anchorMidnight = new Date(
    anchorDate.getFullYear(),
    anchorDate.getMonth(),
    anchorDate.getDate()
  );
  const previousMidnight = new Date(
    previousAnchor.getFullYear(),
    previousAnchor.getMonth(),
    previousAnchor.getDate()
  );

  const totalDays = Math.max(1, (anchorMidnight - previousMidnight) / DAY_IN_MS);
  let coveredDays = (anchorMidnight - startMidnight) / DAY_IN_MS;

  if (coveredDays <= 0) {
    coveredDays = totalDays;
  }

  if (coveredDays > totalDays) {
    coveredDays = totalDays;
  }

  const prorationFactor = coveredDays / totalDays;
  const proratedAmount =
    Math.round((Number(amount) || 0) * prorationFactor * 100) / 100;
  const coverageEnd = new Date(anchorMidnight.getTime() - DAY_IN_MS);

  return {
    anchorDate: anchorMidnight,
    coverageEnd,
    prorationFactor,
    proratedAmount,
  };
}

export default function AddPaymentModal({ onClose, onAdd, organizationId }) {
  const { t } = useCustomTranslation("Recurring Payment");
  const auth = useContext(AuthContext);
  const orgId =
    organizationId ?? auth?.authContext?.accessToken?.organization_id ?? undefined;
  const {
    setShowSuccessAlert,
    setMessageSuccessAlert,
    setShowWarningAlert,
    setMessageWarningAlert,
  } = useSuccessAlert();

  /* Nested modals */
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [isApplyPlanOpen, setIsApplyPlanOpen] = useState(false);

  /* Customer autocomplete state */
  const [customerInput, setCustomerInput] = useState("");
  const debouncedSearch = useDebounced(customerInput, 300);
  const search =
    (typeof debouncedSearch === "string" ? debouncedSearch : "").trim();
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  /* Payment form */
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("Monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isContinuous, setIsContinuous] = useState(false);
  const [reason, setReason] = useState(""); // campaign NAME
  const [selectedCampaignId, setSelectedCampaignId] = useState(""); // campaign ObjectId
  const [taxable, setTaxable] = useState(false);
  const [appliedPlan, setAppliedPlan] = useState(null);
  const [billingAnchorDay, setBillingAnchorDay] = useState("");
  const [firstChargeStrategy, setFirstChargeStrategy] = useState("anchor_only");
  const [activeStep, setActiveStep] = useState(0);

  /* Payment method selection */
  const [selectedMethodId, setSelectedMethodId] = useState("");

  /* Fetch donors */
  const donorsQuery = useApiClientQuery(
    () => api_endpoints.getDonors(search.length >= 2 ? search : "", 1, orgId),
    {
      queryKey: ["getDonors", orgId, { search, sort: 1 }],
      enabled: Boolean(orgId),
    }
  );

  /* Fetch campaigns */
  const campaignsQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaigns(
        orgId,
        "", // campaignName
        "", // campaignCreator
        "", // donationAmount
        "", // status
        "", // acceptCash
        false // is_archived
      ),
    {
      queryKey: ["campaigns", orgId],
      enabled: Boolean(orgId),
      staleTime: 60_000,
    }
  );

  /* Fetch organization for surcharge */
  const orgQuery = useApiClientQuery(
    () => api_endpoints.getOrganization(orgId),
    {
      queryKey: ["organization", orgId],
      enabled: Boolean(orgId),
    }
  );

  const [surchargeEnabled, setSurchargeEnabled] = useState(true);

  useEffect(() => {
    if (orgQuery.data) {
      setSurchargeEnabled(orgQuery.data.is_surcharge_active);
    }
  }, [orgQuery.data]);

  const surchargeAmount = useMemo(() => {
    if (!surchargeEnabled) return 0;
    const rate = orgQuery.data?.surcharge_rate || 0;
    const baseAmount = Number(amount) || 0;
    return baseAmount * rate;
  }, [surchargeEnabled, orgQuery.data, amount]);

  const campaignOptions = useMemo(() => {
    const list = Array.isArray(campaignsQuery.data) ? campaignsQuery.data : [];
    return list.map((c) => ({
      id: c._id || c.campaignId || c.id,
      name: c.name || c.campaignName || "",
    }));
  }, [campaignsQuery.data]);

  /* Reset selected payment method when customer changes */
  useEffect(() => {
    setSelectedMethodId("");
  }, [selectedCustomer?.id]);

  /* Fetch payment methods for the chosen donor */
  const pmQuery = useApiClientQuery(
    () => api_endpoints.listDonorPaymentMethods(selectedCustomer?.id, orgId),
    {
      queryKey: ["donor-payment-methods", selectedCustomer?.id, orgId],
      enabled: Boolean(selectedCustomer?.id && orgId),
    }
  );

  const createSubscriptionMutation = useApiClientMutation(
    (body) => api_endpoints.createRecurringSubscription(body),
    [
      ["recurring-subscriptions", orgId],
      ["recurring-next", orgId],
    ]
  );

  /* Map donors to Autocomplete options */
  const donorOptions = useMemo(() => {
    const list = Array.isArray(donorsQuery.data) ? donorsQuery.data : [];
    const base = list.map((d) => ({
      id: d._id || d.id,
      _id: d._id || d.id,
      ...d,
    }));
    if (
      selectedCustomer &&
      !base.some(
        (o) => (o.id || o._id) === (selectedCustomer.id || selectedCustomer._id)
      )
    ) {
      return [
        {
          ...selectedCustomer,
          id: selectedCustomer.id || selectedCustomer._id,
          _id: selectedCustomer._id || selectedCustomer.id,
        },
        ...base,
      ];
    }
    return base;
  }, [donorsQuery.data, selectedCustomer]);

  /* Resolve selected payment method by id */
  const resolvedMethod = useMemo(() => {
    const list = pmQuery.data || [];
    return list.find((m) => (m._id || m.id) === selectedMethodId) || null;
  }, [pmQuery.data, selectedMethodId]);

  const effectivePlanId = useMemo(() => {
    if (!appliedPlan) return null;
    const hasExpectedAmount =
      typeof appliedPlan.amount === "number" && Number.isFinite(appliedPlan.amount);
    const currentAmount = Number(amount);
    if (
      hasExpectedAmount &&
      Number.isFinite(currentAmount) &&
      currentAmount !== appliedPlan.amount
    ) {
      return null;
    }
    if (appliedPlan.frequency && frequency !== appliedPlan.frequency) {
      return null;
    }
    return appliedPlan.id || null;
  }, [appliedPlan, amount, frequency]);

  /* Submit guard (basic structural checks) */
  const canSubmit = useMemo(() => {
    const n = Number(amount);
    const minAmount = orgQuery.data?.is_surcharge_mandatory
      ? orgQuery.data?.surcharge_rate || 0
      : 0;
    return (
      !!orgId &&
      !!selectedCustomer?.id &&
      !!resolvedMethod &&
      !!startDate &&
      Number.isFinite(n) &&
      n > 0 &&
      n >= minAmount
    );
  }, [orgId, selectedCustomer, resolvedMethod, startDate, amount, orgQuery.data]);

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }),
    []
  );

  const schedulePreview = useMemo(() => {
    const baseStart = parseInputDate(startDate);
    if (!baseStart) return null;

    const normalizedFrequency = frequency;
    if (!normalizedFrequency) return null;

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return null;
    }

    const totalWithSurcharge = numericAmount + Number(surchargeAmount || 0);
    let firstChargeDate = baseStart;
    let nextChargeDate = null;
    let firstPeriodEnd = null;
    let firstChargeTotal = totalWithSurcharge;

    const anchorDay = billingAnchorDay ? Number(billingAnchorDay) : null;
    const resolvedStrategy = anchorDay ? firstChargeStrategy || "anchor_only" : null;

    let proration = null;

    if (normalizedFrequency === "Monthly" && anchorDay) {
      const anchorDate = computeMonthlyAnchorDate(baseStart, anchorDay);
      if (anchorDate) {
        // Check if start date is already on the anchor day (same day)
        const startDay = baseStart.getDate();
        const isStartOnAnchor = startDay === anchorDay;
        
        if (resolvedStrategy === "charge_now_and_anchor") {
          if (isStartOnAnchor) {
            // Edge case: start date IS the anchor day - charge once, not twice
            firstChargeDate = baseStart;
            nextChargeDate = addInterval(baseStart, "Monthly");
            firstChargeTotal = totalWithSurcharge;
            // No proration needed since we're charging the full amount on the anchor day
          } else {
            // Normal case: start date is different from anchor day
            proration = computeChargeNowProrationPreview(baseStart, anchorDay, numericAmount);
            firstChargeDate = baseStart;
            if (proration) {
              nextChargeDate = proration.anchorDate;
              firstPeriodEnd = proration.coverageEnd;
              firstChargeTotal = proration.proratedAmount + Number(surchargeAmount || 0);
            } else {
              nextChargeDate = anchorDate;
            }
          }
        } else {
          firstChargeDate = anchorDate;
          nextChargeDate = addInterval(anchorDate, "Monthly");
        }
      }
    }

    if (!nextChargeDate) {
      nextChargeDate = addInterval(firstChargeDate, normalizedFrequency);
    }

    if (nextChargeDate && nextChargeDate.getTime() === firstChargeDate.getTime()) {
      nextChargeDate = addInterval(nextChargeDate, normalizedFrequency);
    }

    if (!nextChargeDate || Number.isNaN(nextChargeDate.getTime())) {
      return null;
    }

    if (!firstPeriodEnd && nextChargeDate > firstChargeDate) {
      firstPeriodEnd = new Date(nextChargeDate.getTime() - DAY_IN_MS);
    }

    return {
      firstChargeDate,
      nextChargeDate,
      firstPeriodEnd,
      firstChargeTotal,
      nextChargeTotal: totalWithSurcharge,
      anchorDay: anchorDay || null,
      strategy: resolvedStrategy,
      proration,
    };
  }, [
    startDate,
    frequency,
    billingAnchorDay,
    firstChargeStrategy,
    amount,
    surchargeAmount,
  ]);

  const stepDefinitions = useMemo(
    () => [
      {
        key: "customer",
        label: t("step_customer_details", {
          defaultValue: "Customer & Plan",
        }),
      },
      {
        key: "billingSchedule",
        label: t("step_billing_schedule", {
          defaultValue: "Billing & Schedule",
        }),
      },
      {
        key: "payment",
        label: t("step_payment_method", { defaultValue: "Payment Method" }),
      },
      {
        key: "review",
        label: t("step_review", { defaultValue: "Review" }),
      },
    ],
    [t]
  );

  const isLastStep = activeStep === stepDefinitions.length - 1;

  const stepValidity = useMemo(() => {
    const baseAmount = Number(amount);
    const amountValid = Number.isFinite(baseAmount) && baseAmount > 0;
    const hasStartDate = Boolean(parseInputDate(startDate));
    return [
      Boolean(orgId && selectedCustomer?.id && amountValid && frequency),
      hasStartDate,
      Boolean(resolvedMethod),
      canSubmit,
    ];
  }, [
    amount,
    canSubmit,
    frequency,
    orgId,
    resolvedMethod,
    selectedCustomer?.id,
    startDate,
  ]);

  const canAdvance = stepValidity[activeStep];
  const totalWithSurcharge = Number(amount || 0) + Number(surchargeAmount || 0);

  const scheduleErrorMessage = useMemo(() => {
    if (!startDate) return "";
    if (!isDateRangeChronologicallyValid(startDate, endDate, isContinuous)) {
      return t("schedule_range_invalid", {
        defaultValue:
          "End date must be on or after the start date, or left empty for a continuous subscription.",
      });
    }
    if (!isFrequencyRangeLongEnough(frequency, startDate, endDate, isContinuous)) {
      const normalized =
        PLAN_FREQUENCY_LABEL[String(frequency || "").toUpperCase()] ||
        frequency;
      return t("schedule_period_too_short", {
        defaultValue:
          "For {{frequency}} subscriptions, the end date must be at least one full {{frequency}} period after the start date, or left empty for a continuous plan.",
        frequency: normalized,
      });
    }
    return "";
  }, [startDate, endDate, isContinuous, frequency, t]);

  const isScheduleStep = activeStep === 1;
  const nextDisabled =
    !canAdvance ||
    createSubscriptionMutation.isPending ||
    (isScheduleStep && !!scheduleErrorMessage);

  async function handleAdd() {
    // Defensive check: if schedule invalid, send user back to Billing & Schedule
    if (
      !isDateRangeChronologicallyValid(startDate, endDate, isContinuous) ||
      !isFrequencyRangeLongEnough(frequency, startDate, endDate, isContinuous)
    ) {
      setActiveStep(1);
      return;
    }

    if (!canSubmit || !resolvedMethod || !orgId) return;

    const methodType = String(
      resolvedMethod.instrument_type || resolvedMethod.pm_type || ""
    ).toLowerCase();

    let paymentMethodModel = "";
    if (methodType === "ach" || methodType === "bank") {
      paymentMethodModel = "donorBank";
    } else if (methodType === "card") {
      paymentMethodModel = "donorCard";
    }

    const methodId = resolvedMethod._id || resolvedMethod.id;

    if (!paymentMethodModel || !methodId) {
      const fallback = t("subscription_creation_failed", {
        defaultValue: "Failed to create subscription.",
      });
      setMessageWarningAlert(fallback);
      setShowWarningAlert(true);
      return;
    }

    const payload = {
      organization_id: orgId,
      donor_id: selectedCustomer.id,
      amount: Number(amount),
      frequency,
      start_date: startDate,
      end_date: isContinuous ? undefined : endDate || undefined,
      reason: reason || undefined,
      campaign_id: selectedCampaignId || undefined,
      taxable,
      surcharge_enabled: surchargeEnabled,
      surcharge_amount: surchargeAmount,
      payment_method_model: paymentMethodModel,
      payment_method_id: methodId,
    };

    if (frequency === "Monthly" && billingAnchorDay) {
      payload.billing_anchor_day = Number(billingAnchorDay);
      payload.first_charge_strategy = firstChargeStrategy;
    }

    try {
      const created = await createSubscriptionMutation.mutateAsync(payload);
      const successMessage = t("subscription_created_success", {
        defaultValue: "Subscription created successfully.",
      });
      setMessageSuccessAlert(successMessage);
      setShowSuccessAlert(true);

      const createdRow = {
        id: created?._id || created?.id || Math.random().toString(36).slice(2),
        customerId: created?.donor_id || selectedCustomer.id,
        customerName:
          created?.donor_name ||
          selectedCustomer.label ||
          labelForDonor(selectedCustomer),
        paymentMethod: resolvedMethod,
        amount: Number(created?.amount ?? Number(amount) + surchargeAmount),
        frequency: created?.frequency || frequency,
        startDate: created?.start_date?.slice?.(0, 10) || startDate || undefined,
        endDate:
          created?.end_date?.slice?.(0, 10) ||
          (isContinuous ? undefined : endDate || undefined),
        reason: created?.reason ?? reason ?? "",
        taxable: created?.taxable ?? taxable,
        paymentsCount: Number(created?.total_successful ?? 0),
        status: created?.status || "active",
      };

      onAdd?.(createdRow);
    } catch (error) {
      const fallbackMessage = t("subscription_creation_failed", {
        defaultValue: "Failed to create subscription.",
      });
      const apiMessageRaw = error?.response?.data?.message ?? error?.message;
      const apiMessage =
        typeof apiMessageRaw === "string" && apiMessageRaw.trim()
          ? apiMessageRaw
          : fallbackMessage;
      setMessageWarningAlert(apiMessage);
      setShowWarningAlert(true);
      console.error("Failed to create recurring subscription", error);
    }
  }

  const handleNext = () => {
    if (activeStep === 0 && selectedCustomer && !selectedCustomer?.id) {
      setMessageWarningAlert(
        t("failed_to_select_created_customer", {
          defaultValue: "Please select a valid customer before continuing.",
        })
      );
      setShowWarningAlert(true);
      return;
    }

    if (!canAdvance || (isScheduleStep && scheduleErrorMessage)) return;

    if (isLastStep) {
      handleAdd();
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, stepDefinitions.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  function handleApplyPlan(plan) {
    if (!plan) return;
    const planAmount = Number(plan.amount);
    const normalizedFrequency =
      PLAN_FREQUENCY_LABEL[String(plan.frequency || "").toUpperCase()] ||
      plan.frequency;

    setAmount(Number.isFinite(planAmount) ? String(planAmount) : "");
    setFrequency(normalizedFrequency || "");
    if (typeof plan.taxable === "boolean") setTaxable(plan.taxable);

    if (plan.billing_anchor_day) {
      setBillingAnchorDay(String(plan.billing_anchor_day));
    } else {
      setBillingAnchorDay("");
    }
    if (plan.first_charge_strategy) {
      setFirstChargeStrategy(plan.first_charge_strategy);
    } else {
      setFirstChargeStrategy("anchor_only");
    }

    setAppliedPlan({
      id: plan.id || plan._id || null,
      amount: Number.isFinite(planAmount) ? planAmount : null,
      frequency: normalizedFrequency || "",
      name: plan.name || "",
      billing_anchor_day: plan.billing_anchor_day ?? null,
      first_charge_strategy: plan.first_charge_strategy || null,
    });
    if (!reason) {
      setReason(plan.name || "");
    }
    if (plan.campaign_id) {
      setSelectedCampaignId(plan.campaign_id);
    }
  }

  const handleClearPlan = () => {
    setAppliedPlan(null);
    setBillingAnchorDay("");
    setFirstChargeStrategy("anchor_only");
    setSelectedCampaignId("");
    setReason("");
  };

  const customerStep = (
    <Stack spacing={3}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? "grey.50" : "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid container columnSpacing={1.5} rowSpacing={1.5}>
          <Grid item xs>
            <Autocomplete
              fullWidth
              options={donorOptions}
              loading={donorsQuery.isFetching || donorsQuery.isLoading}
              value={selectedCustomer}
              onChange={(_, v) => setSelectedCustomer(v)}
              inputValue={customerInput}
              onInputChange={(_, v) =>
                setCustomerInput(typeof v === "string" ? v : "")
              }
              getOptionLabel={(o) =>
                typeof o === "string" ? o : labelForDonor(o || {})
              }
              isOptionEqualToValue={(o, v) =>
                (o?._id || o?.id) === (v?._id || v?.id)
              }
              filterOptions={(x) => x}
              openOnFocus
              autoHighlight
              noOptionsText={
                search.length >= 2
                  ? t("no_donors_found")
                  : t("type_to_search_donors")
              }
              loadingText={t("loading")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("search_customer_or_business")}
                  placeholder={t("type_at_least_2_characters")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {donorsQuery.isFetching || donorsQuery.isLoading ? (
                          <CircularProgress size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs="auto">
            <Button
              variant="outlined"
              sx={{ minWidth: 180, whiteSpace: "nowrap" }}
              onClick={() => setIsAddCustomerOpen(true)}
            >
              {t("add_new_customer")}
            </Button>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
          {selectedCustomer ? (
            <Stack spacing={0.25}>
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                {t("selected_customer")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {labelForDonor(selectedCustomer)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("no_customer_selected_yet")}
            </Typography>
          )}
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="subtitle2">
            {t("recurring_payment_details")}
          </Typography>
          <Stack direction="row" spacing={1}>
            {appliedPlan && (
              <Button size="small" onClick={handleClearPlan}>
                {t("remove_plan", { defaultValue: "Remove plan" })}
              </Button>
            )}
            <Button variant="outlined" onClick={() => setIsApplyPlanOpen(true)}>
              {appliedPlan
                ? t("change_plan", { defaultValue: "Change plan" })
                : t("apply_payment_plan")}
            </Button>
          </Stack>
        </Stack>

        {appliedPlan && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("plan_applied_summary", {
              defaultValue: "Using plan {{name}}",
              name:
                appliedPlan.name ||
                t("unnamed_plan", { defaultValue: "Unnamed plan" }),
            })}
          </Typography>
        )}

        <Grid container rowSpacing={2} columnSpacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("amount")}
              type="number"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{
                min: orgQuery.data?.is_surcharge_mandatory
                  ? orgQuery.data?.surcharge_rate || 0
                  : 0,
                step: "0.01",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t("frequency")}</InputLabel>
              <Select
                label={t("frequency")}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                <MenuItem value="Weekly">{t("weekly")}</MenuItem>
                <MenuItem value="BiWeekly">{t("biweekly", { defaultValue: "Bi-Weekly" })}</MenuItem>
                <MenuItem value="Monthly">{t("monthly")}</MenuItem>
                <MenuItem value="Quarterly">{t("quarterly")}</MenuItem>
                <MenuItem value="Yearly">{t("yearly")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("reason_campaign_or_other_object")}</InputLabel>
              <Select
                label={t("reason_campaign_or_other_object")}
                value={selectedCampaignId}
                onChange={(e) => {
                  const campaignId = e.target.value;
                  setSelectedCampaignId(campaignId);
                  const campaign = campaignOptions.find((c) => c.id === campaignId);
                  setReason(campaign?.name || "");
                }}
                disabled={campaignsQuery.isLoading}
              >
                <MenuItem value="">
                  <em>{t("select_a_campaign")}</em>
                </MenuItem>
                {campaignOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              {reason && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("selected_reason", { defaultValue: "Reason" })}: {reason}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={surchargeEnabled}
                  onChange={(e) => setSurchargeEnabled(e.target.checked)}
                  disabled={orgQuery.data?.is_surcharge_mandatory}
                />
              }
              label={t("include_surcharge")}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={taxable}
                  onChange={(e) => setTaxable(e.target.checked)}
                />
              }
              label={t("taxable")}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: "bold" }}
              >
                {t("surcharge")}: ${formatCurrency(surchargeAmount)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: "bold" }}
              >
                {t("total")}: ${formatCurrency(totalWithSurcharge)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );

  const billingScheduleStep = (
    <Stack spacing={2}>
      {frequency === "Monthly" ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">
            {t("billing_anchor_settings", {
              defaultValue: "Billing anchor & strategy",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("billing_anchor_day_helper", {
              defaultValue:
                "Choose the anchor day and first charge behaviour for monthly billing.",
            })}
          </Typography>
          <Grid container rowSpacing={2} columnSpacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("billing_anchor_day")}
                type="number"
                fullWidth
                value={billingAnchorDay}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || (Number(val) >= 1 && Number(val) <= 28)) {
                    setBillingAnchorDay(val);
                  }
                }}
                inputProps={{ min: 1, max: 28 }}
                helperText={t("billing_anchor_day_helper")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label={t("first_charge_strategy")}
                value={firstChargeStrategy}
                onChange={(e) => setFirstChargeStrategy(e.target.value)}
                disabled={!billingAnchorDay}
              >
                <MenuItem value="anchor_only">{t("anchor_only")}</MenuItem>
                <MenuItem value="charge_now_and_anchor">
                  {t("charge_now_and_anchor")}
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("billing_anchor_not_available", {
              defaultValue:
                "Billing anchors and charge-now strategy apply to monthly frequency only.",
            })}
          </Typography>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">
          {t("schedule_dates", { defaultValue: "Schedule dates" })}
        </Typography>
        <Grid container rowSpacing={2} columnSpacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t("start_date")}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={1}>
              {!isContinuous && (
                <TextField
                  label={t("end_date")}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              )}
              <Button
                variant="text"
                sx={{ alignSelf: "flex-start" }}
                onClick={() =>
                  setIsContinuous((prev) => {
                    const next = !prev;
                    if (next) {
                      setEndDate("");
                    }
                    return next;
                  })
                }
              >
                {isContinuous
                  ? t("set_end_date", { defaultValue: "Add end date" })
                  : t("make_continuous", { defaultValue: "Make continuous" })}
              </Button>
            </Stack>
          </Grid>
        </Grid>
        {isContinuous && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            {t("continuous_subscription_hint", {
              defaultValue: "This subscription will continue until cancelled.",
            })}
          </Typography>
        )}
        {scheduleErrorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {scheduleErrorMessage}
          </Alert>
        )}
      </Paper>

      {schedulePreview && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">
            {t("billing_schedule_preview", {
              defaultValue: "Billing schedule",
            })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t("first_charge_preview", {
              defaultValue: "First charge on {{date}} for ${{amount}}",
              date: dateFormatter.format(schedulePreview.firstChargeDate),
              amount: formatCurrency(schedulePreview.firstChargeTotal),
            })}
          </Typography>
          {schedulePreview.firstPeriodEnd && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {t("first_period_range", {
                defaultValue: "Covers {{start}} through {{end}}.",
                start: dateFormatter.format(schedulePreview.firstChargeDate),
                end: schedulePreview.firstPeriodEnd
                  ? dateFormatter.format(schedulePreview.firstPeriodEnd)
                  : "",
              })}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            {t("next_charge_preview", {
              defaultValue: "Next charge on {{date}} for ${{amount}}",
              date: dateFormatter.format(schedulePreview.nextChargeDate),
              amount: formatCurrency(schedulePreview.nextChargeTotal),
            })}
          </Typography>
          {schedulePreview.strategy && schedulePreview.anchorDay && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {schedulePreview.strategy === "charge_now_and_anchor"
                ? t("charge_now_anchor_hint", {
                    defaultValue:
                      "Charges immediately, then every month on day {{day}}.",
                    day: schedulePreview.anchorDay,
                  })
                : t("anchor_only_hint", {
                    defaultValue: "Charges begin on day {{day}} of each month.",
                    day: schedulePreview.anchorDay,
                  })}
            </Typography>
          )}
          {schedulePreview.proration && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {t("proration_summary", {
                defaultValue:
                  "Prorated at {{factor}} for a charge of ${{amount}}.",
                factor: schedulePreview.proration.prorationFactor.toFixed(4),
                amount: formatCurrency(schedulePreview.proration.proratedAmount),
              })}
            </Typography>
          )}
        </Paper>
      )}
    </Stack>
  );

  const paymentStep = (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2">
        {t("payment_method", { defaultValue: "Payment method" })}
      </Typography>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={2}
        alignItems="center"
        sx={{ mt: 1 }}
      >
        <Grid item xs>
          <FormControl fullWidth>
            <InputLabel>{t("payment_method")}</InputLabel>
            <Select
              label={t("payment_method")}
              value={selectedMethodId}
              onChange={(e) => setSelectedMethodId(e.target.value)}
              disabled={
                !selectedCustomer ||
                pmQuery.isFetching ||
                !(pmQuery.data || []).length
              }
            >
              <MenuItem value="" disabled>
                {selectedCustomer
                  ? pmQuery.isFetching
                    ? t("loading_methods")
                    : (pmQuery.data || []).length
                    ? t("select_a_saved_method")
                    : t("no_saved_methods_found")
                  : t("select_a_customer_first")}
              </MenuItem>
              {(pmQuery.data || []).map((m) => (
                <MenuItem key={m._id || m.id} value={m._id || m.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {labelForPaymentMethod(m)}
                    </Typography>
                    <Chip 
                      label={isCard(m) ? t("card", { defaultValue: "Card" }) : t("bank", { defaultValue: "Bank" })} 
                      size="small" 
                      color={isCard(m) ? "primary" : "secondary"}
                      variant="outlined"
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs="auto">
          <Button
            variant="outlined"
            sx={{ minWidth: 220, whiteSpace: "nowrap" }}
            onClick={() => setIsAddMethodOpen(true)}
            disabled={!selectedCustomer}
          >
            {t("add_new_payment_method")}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 1.5, color: "text.secondary" }}>
            {resolvedMethod ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {t("selected_method")}
                </Typography>
                <Typography variant="body2">
                  {labelForPaymentMethod(resolvedMethod)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2">
                {t("payment_method_card")}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );

  const customerLabel = selectedCustomer
    ? labelForDonor(selectedCustomer)
    : t("not_selected", { defaultValue: "Not selected" });
  const reasonLabel = reason
    ? reason
    : t("not_provided", { defaultValue: "Not provided" });
  const planLabel = appliedPlan
    ? appliedPlan.name || t("unnamed_plan", { defaultValue: "Unnamed plan" })
    : t("no_plan_selected", { defaultValue: "No plan selected" });
  const paymentMethodLabel = resolvedMethod
    ? labelForPaymentMethod(resolvedMethod)
    : t("not_selected", { defaultValue: "Not selected" });
  const firstChargeDateLabel = schedulePreview
    ? dateFormatter.format(schedulePreview.firstChargeDate)
    : t("pending", { defaultValue: "Pending" });
  const firstChargeAmountLabel = schedulePreview
    ? formatCurrency(schedulePreview.firstChargeTotal)
    : formatCurrency(totalWithSurcharge);
  const nextChargeDateLabel = schedulePreview
    ? dateFormatter.format(schedulePreview.nextChargeDate)
    : t("pending", { defaultValue: "Pending" });
  const nextChargeAmountLabel = schedulePreview
    ? formatCurrency(schedulePreview.nextChargeTotal)
    : formatCurrency(totalWithSurcharge);
  const firstPeriodEndLabel = schedulePreview?.firstPeriodEnd
    ? dateFormatter.format(schedulePreview.firstPeriodEnd)
    : null;
  const startDateLabel = startDate
    ? dateFormatter.format(parseInputDate(startDate))
    : t("not_set", { defaultValue: "Not set" });
  const endDateLabel = isContinuous
    ? t("continuous", { defaultValue: "Continuous" })
    : endDate
    ? dateFormatter.format(parseInputDate(endDate))
    : t("not_set", { defaultValue: "Not set" });
  const anchorLabel = billingAnchorDay
    ? t("anchor_day_format", {
        defaultValue: "Day {{day}}",
        day: billingAnchorDay,
      })
    : t("not_set", { defaultValue: "Not set" });
  const strategyLabel = billingAnchorDay
    ? firstChargeStrategy
    : t("not_applicable", { defaultValue: "Not applicable" });
  const surchargeLabel = surchargeEnabled
    ? `$${formatCurrency(surchargeAmount)}`
    : t("not_applied", { defaultValue: "Not applied" });

  const reviewItems = [
    { label: t("customer", { defaultValue: "Customer" }), value: customerLabel },
    { label: t("reason", { defaultValue: "Reason" }), value: reasonLabel },
    { label: t("plan", { defaultValue: "Plan" }), value: planLabel },
    {
      label: t("amount", { defaultValue: "Amount" }),
      value: `$${formatCurrency(amount)}`,
    },
    {
      label: t("surcharge", { defaultValue: "Surcharge" }),
      value: surchargeLabel,
    },
    {
      label: t("total_with_surcharge", {
        defaultValue: "Total (incl. surcharge)",
      }),
      value: `$${formatCurrency(totalWithSurcharge)}`,
    },
    {
      label: t("taxable", { defaultValue: "Taxable" }),
      value: taxable
        ? t("yes", { defaultValue: "Yes" })
        : t("no", { defaultValue: "No" }),
    },
    {
      label: t("frequency", { defaultValue: "Frequency" }),
      value: frequency,
    },
    {
      label: t("billing_anchor_day", {
        defaultValue: "Billing anchor day",
      }),
      value: anchorLabel,
    },
    {
      label: t("first_charge_strategy", {
        defaultValue: "First charge strategy",
      }),
      value: strategyLabel,
    },
    {
      label: t("start_date", { defaultValue: "Start date" }),
      value: startDateLabel,
    },
    {
      label: t("end_date", { defaultValue: "End date" }),
      value: endDateLabel,
    },
    {
      label: t("payment_method", { defaultValue: "Payment method" }),
      value: paymentMethodLabel,
    },
  ];

  const reviewStep = (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">
          {t("subscription_summary", { defaultValue: "Subscription summary" })}
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {reviewItems.map(({ label, value }) => (
            <Box
              key={label}
              sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, textAlign: "right" }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">
          {t("charge_preview", { defaultValue: "Charge preview" })}
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <Typography variant="body2">
            <strong>{t("customer", { defaultValue: "Customer" })}:</strong>{" "}
            {customerLabel}
          </Typography>
          <Typography variant="body2">
            <strong>{t("reason", { defaultValue: "Reason" })}:</strong>{" "}
            {reasonLabel}
          </Typography>
          <Typography variant="body2">
            <strong>
              {t("total_with_surcharge", {
                defaultValue: "Total (incl. surcharge)",
              })}
              :
            </strong>{" "}
            ${formatCurrency(totalWithSurcharge)}
          </Typography>
          <Typography variant="body2">
            <strong>{t("first_charge", { defaultValue: "First charge" })}:</strong>{" "}
            {firstChargeDateLabel} · ${firstChargeAmountLabel}
          </Typography>
          {firstPeriodEndLabel && schedulePreview && (
            <Typography variant="caption" color="text.secondary">
              {t("first_period_range", {
                defaultValue: "Covers {{start}} through {{end}}.",
                start: dateFormatter.format(schedulePreview.firstChargeDate),
                end: firstPeriodEndLabel,
              })}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>{t("next_charge", { defaultValue: "Next charge" })}:</strong>{" "}
            {nextChargeDateLabel} · ${nextChargeAmountLabel}
          </Typography>
          {schedulePreview?.proration && (
            <Typography variant="caption" color="text.secondary">
              {t("proration_summary", {
                defaultValue:
                  "Prorated at {{factor}} for a charge of ${{amount}}.",
                factor: schedulePreview.proration.prorationFactor.toFixed(4),
                amount: formatCurrency(schedulePreview.proration.proratedAmount),
              })}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>{t("payment_method", { defaultValue: "Payment method" })}:</strong>{" "}
            {paymentMethodLabel}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );

  const stepContent = [customerStep, billingScheduleStep, paymentStep, reviewStep];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {t("add_payment")}
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          {t("choose_customer_to_unlock_payment_methods")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepDefinitions.map((step) => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ mt: 3 }}>{stepContent[activeStep]}</Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          color="error"
          onClick={onClose}
          disabled={createSubscriptionMutation.isPending}
        >
          {t("cancel")}
        </Button>
        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            disabled={createSubscriptionMutation.isPending}
          >
            {t("back", { defaultValue: "Back" })}
          </Button>
        )}
        <Button variant="contained" onClick={handleNext} disabled={nextDisabled}>
          {isLastStep
            ? createSubscriptionMutation.isPending
              ? t("processing", { defaultValue: "Processing..." })
              : t("confirm", { defaultValue: "Confirm" })
            : t("next", { defaultValue: "Next" })}
        </Button>
      </DialogActions>

      {isAddCustomerOpen && (
        <AddCustomerModal
          organizationId={orgId}
          onClose={() => setIsAddCustomerOpen(false)}
          onSave={(donor) => {
            const donorId = donor?.id || donor?._id;

            if (!donorId) {
              setMessageWarningAlert(
                t("failed_to_select_created_customer", {
                  defaultValue: "Customer was created, but could not be selected.",
                })
              );
              setShowWarningAlert(true);
              return false;
            }

            const opt = {
              ...donor,
              id: donorId,
              _id: donorId,
            };
            setSelectedCustomer(opt);
            setCustomerInput(
              `${opt.first_name || ""} ${opt.last_name || ""}`.trim() ||
                opt.email ||
                ""
            );
            donorsQuery.refetch?.();
            setIsAddCustomerOpen(false);
            return true;
          }}
        />
      )}

      {isAddMethodOpen && (
        <AddPaymentMethodModal
          donorId={selectedCustomer?.id}
          organizationId={orgId}
          onClose={() => setIsAddMethodOpen(false)}
          onSave={async (resp) => {
            setSelectedMethodId(resp._id || resp.id);
            setIsAddMethodOpen(false);
            await pmQuery.refetch?.();
          }}
        />
      )}

      {isApplyPlanOpen && (
        <ApplyPaymentPlanModal
          orgId={orgId}
          onOpen={isApplyPlanOpen}
          onClose={() => setIsApplyPlanOpen(false)}
          onApply={handleApplyPlan}
        />
      )}
    </Dialog>
  );
}
